import type { CSSProperties } from 'react'
import type { DashboardWindow } from '../api/client'

/**
 * Time range control: relative presets OR an absolute from/to pair.
 *
 * The absolute inputs are `datetime-local`, so their value is a LOCAL wall-clock string
 * with no zone ("2026-09-03T19:08"). It is converted to UTC ISO8601 exactly once, in
 * `toWindow()`, because the backend contract takes ISO8601 — passing the raw local
 * string through would silently shift the window by the browser's offset.
 */
export type TimeRange =
  | { mode: 'relative'; hours: number }
  | { mode: 'absolute'; from: string; to: string }

export const RELATIVE_PRESETS: readonly { hours: number; label: string }[] = [
  { hours: 1, label: 'Last 1 hour' },
  { hours: 6, label: 'Last 6 hours' },
  { hours: 24, label: 'Last 24 hours' },
  { hours: 168, label: 'Last 7 days' },
  { hours: 720, label: 'Last 30 days' },
]

/** True only when an absolute range is fully specified and ordered. */
export function isCompleteAbsolute(range: TimeRange): boolean {
  if (range.mode !== 'absolute') return false
  if (!range.from || !range.to) return false
  const from = new Date(range.from).getTime()
  const to = new Date(range.to).getTime()
  return Number.isFinite(from) && Number.isFinite(to) && from < to
}

/**
 * Collapse the control's state into the API's window argument.
 *
 * An incomplete or inverted absolute range yields the relative fallback rather than a
 * one-sided window: the request stays valid while the operator is still typing.
 */
export function toWindow(range: TimeRange, fallbackHours = 24): DashboardWindow {
  if (isCompleteAbsolute(range) && range.mode === 'absolute') {
    return { from: new Date(range.from).toISOString(), to: new Date(range.to).toISOString() }
  }
  return { hours: range.mode === 'relative' ? range.hours : fallbackHours }
}

/** Stable cache key so a fetch effect re-runs on value change, not object identity. */
export function windowKey(win: DashboardWindow): string {
  return win.from && win.to ? `abs:${win.from}:${win.to}` : `rel:${win.hours ?? 24}`
}

const inputStyle: CSSProperties = {
  padding: '0.35rem',
  background: '#1e1e1e',
  color: '#f5f5f5',
  border: '1px solid #333',
  borderRadius: '4px',
}

export default function TimeRangePicker({
  value,
  onChange,
}: {
  value: TimeRange
  onChange: (next: TimeRange) => void
}) {
  const selected = value.mode === 'absolute' ? 'custom' : String(value.hours)

  function handleSelect(raw: string) {
    if (raw === 'custom') {
      onChange({ mode: 'absolute', from: '', to: '' })
    } else {
      onChange({ mode: 'relative', hours: Number(raw) })
    }
  }

  return (
    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
      <select value={selected} onChange={e => handleSelect(e.target.value)} style={inputStyle}>
        {RELATIVE_PRESETS.map(p => (
          <option key={p.hours} value={p.hours}>{p.label}</option>
        ))}
        <option value="custom">Custom range…</option>
      </select>

      {value.mode === 'absolute' && (
        <>
          <input
            type="datetime-local"
            aria-label="Range start"
            value={value.from}
            onChange={e => onChange({ ...value, from: e.target.value })}
            style={inputStyle}
          />
          <span style={{ color: '#9aa0a6' }}>→</span>
          <input
            type="datetime-local"
            aria-label="Range end"
            value={value.to}
            onChange={e => onChange({ ...value, to: e.target.value })}
            style={inputStyle}
          />
          {!isCompleteAbsolute(value) && (
            <span style={{ color: '#ff9800', fontSize: '0.8rem' }}>
              showing last 24h until both ends are set
            </span>
          )}
        </>
      )}
    </div>
  )
}
